import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, Button,
  Chip, CircularProgress, Alert, TextField, Dialog,
  DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import { getSuggestions, decideSuggestion } from '../services/api';

const priorityColor = { low: '#6b7280', normal: '#2563eb', high: '#dc2626' };
const confidenceColor = (score) => {
  if (score >= 80) return '#16a34a';
  if (score >= 60) return '#d97706';
  return '#dc2626';
};

export default function Suggestions() {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialog, setDialog] = useState({ open: false, id: null, decision: '' });
  const [reason, setReason] = useState('');

  const load = async () => {
    try {
      const res = await getSuggestions();
      setSuggestions(res.data);
    } catch {
      setError('Failed to load suggestions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openDialog = (id, decision) => {
    setDialog({ open: true, id, decision });
    setReason('');
  };

  const handleDecide = async () => {
    try {
      await decideSuggestion(dialog.id, dialog.decision, reason);
      setDialog({ open: false, id: null, decision: '' });
      load();
    } catch {
      setError('Failed to submit decision');
    }
  };

  if (loading) return (
    <Box display="flex" justifyContent="center" mt={4}>
      <CircularProgress sx={{ color: '#2563eb' }} />
    </Box>
  );

  return (
    <Box>
      <Typography variant="h5" fontWeight="bold" color="#fff" mb={3}>
        AI Suggestions
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {suggestions.length === 0 && (
        <Card sx={{ background: '#1a2633' }}>
          <CardContent>
            <Typography color="#8899aa">No pending suggestions</Typography>
          </CardContent>
        </Card>
      )}
      {suggestions.map((s) => (
        <Card key={s.id} sx={{ background: '#1a2633', mb: 2, border: '1px solid #2d3f50' }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Box display="flex" gap={1}>
                <Chip
                  label={s.priority?.toUpperCase()}
                  size="small"
                  sx={{ background: priorityColor[s.priority] || '#6b7280', color: '#fff' }}
                />
                <Chip
                  label={s.type}
                  size="small"
                  sx={{ background: '#2d3f50', color: '#8899aa' }}
                />
              </Box>
              <Typography
                variant="body2" fontWeight="bold"
                sx={{ color: confidenceColor(s.confidence_score) }}
              >
                Confidence: {s.confidence_score}%
              </Typography>
            </Box>
            <Typography color="#fff" fontWeight="bold" mb={1}>
              {s.problem}
            </Typography>
            <Typography color="#8899aa" variant="body2" mb={2}>
              ID: {s.id} — Created: {new Date(s.created_at).toLocaleString()}
            </Typography>
            <Box display="flex" gap={1}>
              <Button
                variant="contained" size="small"
                sx={{ background: '#16a34a' }}
                onClick={() => openDialog(s.id, 'approved')}
              >
                Approve
              </Button>
              <Button
                variant="contained" size="small"
                sx={{ background: '#dc2626' }}
                onClick={() => openDialog(s.id, 'rejected')}
              >
                Reject
              </Button>
              <Button
                variant="outlined" size="small"
                sx={{ color: '#d97706', borderColor: '#d97706' }}
                onClick={() => openDialog(s.id, 'escalated')}
              >
                Escalate
              </Button>
            </Box>
          </CardContent>
        </Card>
      ))}

      <Dialog open={dialog.open} onClose={() => setDialog({ open: false, id: null, decision: '' })}>
        <DialogTitle sx={{ background: '#1a2633', color: '#fff' }}>
          Confirm: {dialog.decision?.toUpperCase()}
        </DialogTitle>
        <DialogContent sx={{ background: '#1a2633' }}>
          <TextField
            fullWidth multiline rows={3}
            label="Reason (optional)"
            value={reason}
            onChange={e => setReason(e.target.value)}
            sx={{ mt: 2, input: { color: '#fff' }, label: { color: '#8899aa' } }}
          />
        </DialogContent>
        <DialogActions sx={{ background: '#1a2633' }}>
          <Button onClick={() => setDialog({ open: false, id: null, decision: '' })}
            sx={{ color: '#8899aa' }}>
            Cancel
          </Button>
          <Button onClick={handleDecide} variant="contained"
            sx={{ background: '#2563eb' }}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
